import type { PickerEntry } from "../types";

const UINT32_RANGE = 4_294_967_296;

export type RandomUint32 = () => number;

export function secureRandomUint32(): number {
  const buffer = new Uint32Array(1);
  crypto.getRandomValues(buffer);
  return buffer[0] ?? 0;
}

export function uniformInt(
  maxExclusive: number,
  randomUint32: RandomUint32 = secureRandomUint32,
): number {
  if (!Number.isSafeInteger(maxExclusive) || maxExclusive <= 0 || maxExclusive > UINT32_RANGE) {
    throw new RangeError("maxExclusive must be a positive safe integer no greater than 2^32.");
  }

  const limit = UINT32_RANGE - (UINT32_RANGE % maxExclusive);
  let value = randomUint32() >>> 0;
  while (value >= limit) {
    value = randomUint32() >>> 0;
  }
  return value % maxExclusive;
}

export function selectWeightedEntry(
  entries: PickerEntry[],
  randomUint32: RandomUint32 = secureRandomUint32,
): PickerEntry {
  if (entries.length === 0) {
    throw new Error("Cannot select from an empty entry list.");
  }

  const totalWeight = entries.reduce((total, entry) => {
    if (!Number.isSafeInteger(entry.weight) || entry.weight < 1 || entry.weight > 1_000) {
      throw new RangeError(`Invalid weight for ${entry.name}.`);
    }
    return total + entry.weight;
  }, 0);

  const ticket = uniformInt(totalWeight, randomUint32);
  let cursor = 0;
  for (const entry of entries) {
    cursor += entry.weight;
    if (ticket < cursor) return entry;
  }

  return entries[entries.length - 1] as PickerEntry;
}
